import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import {
  ArrowLeft, Download, Users, CheckCircle, TrendingUp, ChevronDown, ChevronUp, BarChart3, MessageSquare
} from 'lucide-react';

// Stat Card Component (OEM Style)
const StatCard = ({ icon: Icon, value, label, iconBg, valueColor }) => (
  <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg || 'bg-slate-100'}`}>
        <Icon className="h-5 w-5 text-slate-600" />
      </div>
      <div>
        <p className={`text-2xl font-black ${valueColor || 'text-slate-900'}`}>{value}</p>
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</p>
      </div>
    </div>
  </div>
);

const SurveyResults = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    fetchResults();
  }, [surveyId]);

  const showToastNotification = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const fetchResults = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/surveys/${surveyId}/results/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResults(response.data);
      const expanded = {};
      response.data.questions_stats.forEach(q => {
        expanded[q.question_id] = true;
      });
      setExpandedQuestions(expanded);
    } catch (error) {
      console.error('Error fetching results:', error);
      showToastNotification('Error loading results');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/surveys/${surveyId}/results/export/`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${results.survey_title}_results.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      showToastNotification('Export downloaded successfully');
    } catch (error) {
      console.error('Error exporting results:', error);
      showToastNotification('Error exporting results');
    }
  };

  const toggleQuestion = (questionId) => {
    setExpandedQuestions({
      ...expandedQuestions,
      [questionId]: !expandedQuestions[questionId]
    });
  };

  const renderChoiceChart = (optionCounts, totalAnswers) => {
    const entries = Object.entries(optionCounts).sort((a, b) => b[1] - a[1]);
    const maxCount = Math.max(...entries.map(e => e[1]), 1);

    return (
      <div className="space-y-3">
        {entries.map(([option, count]) => {
          const percentage = totalAnswers > 0 ? (count / totalAnswers * 100).toFixed(1) : 0;
          const barWidth = (count / maxCount * 100);

          return (
            <div key={option}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-bold text-slate-700 truncate mr-4">{option}</span>
                <span className="text-xs font-bold text-slate-500 whitespace-nowrap">{count} ({percentage}%)</span>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-400 to-orange-500 rounded-full transition-all"
                  style={{ width: `${barWidth}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderRatingChart = (distribution, average, totalAnswers) => {
    const entries = Object.entries(distribution)
      .map(([rating, count]) => [parseInt(rating), count])
      .sort((a, b) => a[0] - b[0]);
    const maxCount = Math.max(...entries.map(e => e[1]), 1);

    return (
      <div className="flex items-center gap-6">
        <div className="text-center">
          <p className="text-4xl font-black text-orange-500">{average?.toFixed(1) || 0}</p>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Average</p>
        </div>
        <div className="flex-1 flex items-end gap-1 h-20">
          {entries.map(([rating, count]) => {
            const barHeight = (count / maxCount * 100);
            return (
              <div key={rating} className="flex-1 flex flex-col items-center">
                <div className="w-full bg-slate-100 rounded-t overflow-hidden flex-1 flex flex-col justify-end">
                  <div
                    className="bg-orange-400 rounded-t transition-all"
                    style={{ height: `${barHeight}%` }}
                  />
                </div>
                <span className="text-[10px] font-bold text-slate-500 mt-1">{rating}</span>
                <span className="text-[9px] text-slate-400">{count}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderNPSChart = (distribution, average, totalAnswers) => {
    const detractors = [0, 1, 2, 3, 4, 5, 6].reduce((sum, n) => sum + (distribution[n] || 0), 0);
    const passives = [7, 8].reduce((sum, n) => sum + (distribution[n] || 0), 0);
    const promoters = [9, 10].reduce((sum, n) => sum + (distribution[n] || 0), 0);

    const npsScore = totalAnswers > 0
      ? Math.round((promoters / totalAnswers - detractors / totalAnswers) * 100)
      : 0;

    return (
      <div className="space-y-4">
        <div className="text-center">
          <p className={`text-4xl font-black ${npsScore >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
            {npsScore > 0 ? '+' : ''}{npsScore}
          </p>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">NPS Score</p>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div className="p-3 bg-red-50 rounded-xl text-center">
            <p className="text-[10px] font-bold text-red-600 uppercase tracking-wider mb-1">Detractors</p>
            <p className="text-xl font-black text-red-600">{detractors}</p>
            <p className="text-[9px] text-red-500">{totalAnswers > 0 ? ((detractors / totalAnswers) * 100).toFixed(0) : 0}%</p>
          </div>
          <div className="p-3 bg-amber-50 rounded-xl text-center">
            <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-1">Passives</p>
            <p className="text-xl font-black text-amber-600">{passives}</p>
            <p className="text-[9px] text-amber-500">{totalAnswers > 0 ? ((passives / totalAnswers) * 100).toFixed(0) : 0}%</p>
          </div>
          <div className="p-3 bg-emerald-50 rounded-xl text-center">
            <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-1">Promoters</p>
            <p className="text-xl font-black text-emerald-600">{promoters}</p>
            <p className="text-[9px] text-emerald-500">{totalAnswers > 0 ? ((promoters / totalAnswers) * 100).toFixed(0) : 0}%</p>
          </div>
        </div>
      </div>
    );
  };

  const renderLikertChart = (statementStats) => {
    const statements = Object.entries(statementStats);
    const colors = ['bg-red-400', 'bg-orange-400', 'bg-amber-400', 'bg-lime-400', 'bg-emerald-400'];

    return (
      <div className="space-y-3">
        {statements.map(([statement, options]) => {
          const totalForStatement = Object.values(options).reduce((sum, count) => sum + count, 0);
          const optionEntries = Object.entries(options);

          return (
            <div key={statement}>
              <p className="text-sm font-bold text-slate-700 mb-1 truncate">{statement}</p>
              <div className="h-6 bg-slate-100 rounded-lg overflow-hidden flex">
                {optionEntries.map(([option, count], idx) => {
                  const width = totalForStatement > 0 ? (count / totalForStatement * 100) : 0;
                  return (
                    <div
                      key={option}
                      className={`${colors[idx % colors.length]} flex items-center justify-center text-[9px] font-bold text-white`}
                      style={{ width: `${width}%` }}
                      title={`${option}: ${count} (${width.toFixed(0)}%)`}
                    >
                      {width > 12 && count}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {statements.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-slate-100">
            {Object.keys(statements[0][1]).map((option, idx) => (
              <span key={option} className="flex items-center gap-1.5 text-[10px] text-slate-600">
                <span className={`w-2.5 h-2.5 rounded ${colors[idx % colors.length]}`} />
                {option}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderTextAnswers = (answers) => {
    return (
      <div>
        {answers.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No text answers yet</p>
        ) : (
          <ul className="space-y-2 max-h-60 overflow-y-auto">
            {answers.map((answer, idx) => (
              <li key={idx} className="p-3 bg-slate-50 rounded-xl text-sm text-slate-700 border-l-2 border-orange-400">
                {answer}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const renderQuestionStats = (questionStat) => {
    const isExpanded = expandedQuestions[questionStat.question_id];

    return (
      <div key={questionStat.question_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div
          className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
          onClick={() => toggleQuestion(questionStat.question_id)}
        >
          <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
            <BarChart3 size={14} className="text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="px-2 py-0.5 text-[9px] font-bold bg-slate-100 text-slate-500 rounded-md uppercase">
                {questionStat.question_type}
              </span>
              <span className="text-[10px] font-bold text-slate-400">
                {questionStat.total_answers} responses
              </span>
            </div>
            <p className="text-sm font-bold text-slate-900 truncate">{questionStat.question_text}</p>
          </div>
          {isExpanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>

        {isExpanded && (
          <div className="px-4 pb-4 border-t border-slate-100 pt-4">
            {questionStat.question_type === 'choice' && (
              renderChoiceChart(questionStat.option_counts || {}, questionStat.total_answers)
            )}

            {questionStat.question_type === 'rating' && (
              renderRatingChart(questionStat.distribution || {}, questionStat.average, questionStat.total_answers)
            )}

            {questionStat.question_type === 'nps' && (
              renderNPSChart(questionStat.distribution || {}, questionStat.average, questionStat.total_answers)
            )}

            {questionStat.question_type === 'likert' && (
              renderLikertChart(questionStat.statement_stats || {})
            )}

            {questionStat.question_type === 'text' && (
              renderTextAnswers(questionStat.answers || [])
            )}

            {questionStat.question_type === 'project_select' && (
              renderChoiceChart(questionStat.option_counts || {}, questionStat.total_answers)
            )}

            {questionStat.question_type === 'user_select' && (
              renderChoiceChart(questionStat.option_counts || {}, questionStat.total_answers)
            )}

            {!['choice', 'rating', 'nps', 'likert', 'text', 'project_select', 'user_select'].includes(questionStat.question_type) && (
              <p className="text-sm text-slate-400 text-center py-4">Visualization not available for this question type</p>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm border border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm font-medium">Loading results...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl p-10 text-center shadow-sm border border-slate-100">
          <p className="text-sm font-bold text-slate-900">Could not load results</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/surveys')} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
              <ArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-sm font-black text-slate-900">{results.survey_title}</h1>
              <span className="text-[10px] text-slate-400 font-medium">Survey Results</span>
            </div>
          </div>
          <button
            onClick={handleExport}
            className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 flex items-center gap-1.5 font-bold uppercase tracking-wide text-[10px] transition-all shadow-lg shadow-orange-500/20"
          >
            <Download size={14} /> Export to Excel
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-5">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <StatCard
            icon={Users}
            value={results.total_assignments}
            label="Assigned"
            iconBg="bg-slate-100"
          />
          <StatCard
            icon={CheckCircle}
            value={results.total_responses}
            label="Responses"
            iconBg="bg-emerald-100"
          />
          <StatCard
            icon={TrendingUp}
            value={`${results.completion_rate}%`}
            label="Completion Rate"
            iconBg="bg-orange-100"
            valueColor={results.completion_rate >= 70 ? 'text-emerald-500' : results.completion_rate >= 40 ? 'text-amber-500' : 'text-red-500'}
          />
        </div>

        {/* Questions Stats */}
        <div className="space-y-3">
          {results.questions_stats.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-10 text-center">
              <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                <MessageSquare className="h-6 w-6 text-slate-400" />
              </div>
              <p className="text-sm font-bold text-slate-900 mb-1">No responses yet</p>
              <p className="text-xs text-slate-400">Responses will appear here once users complete the survey</p>
            </div>
          ) : (
            results.questions_stats.map(questionStat => renderQuestionStats(questionStat))
          )}
        </div>
      </div>

      {/* Toast */}
      {showToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 bg-slate-900 text-white text-sm font-bold rounded-xl shadow-lg">
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default SurveyResults;
